import { FunctionComponent, useContext, useEffect, useState } from "react";
import { Box, Paper, Typography, useMediaQuery, useTheme, CircularProgress } from "@mui/material";
import { displayMoney, moneyToMetric, Money } from "../../../../utils/money";
import { green, red } from "@mui/material/colors";
import axios from "axios";
import { API_ENDPOINT } from "../../../../configurations/global.config";
import { AuthContext } from "../../../../contexts/user-context/user-context";
import { DriverInvoicePurpose } from "../../../../interfaces/common/driver-invoices";

interface DriverAmountsSummaryProps {
  deliveryTaskIds: number[];
  pickupTaskIds: number[];
  driverId?: number;
  purpose: DriverInvoicePurpose
}

const DriverAmountsSummary: FunctionComponent<DriverAmountsSummaryProps> = ({
  deliveryTaskIds,
  pickupTaskIds,
  driverId,
  purpose
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { config } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [totalCOD, setTotalCOD] = useState<Money[]>([]);
  const [totalCommission, setTotalCommission] = useState<Money[]>([]);
  const [totalPOP, setTotalPOP] = useState<Money[]>([]);
  const [netIncoming, setNetIncoming] = useState<Money[]>([]);

  useEffect(() => {
    const fetchAmounts = async () => {
      if (deliveryTaskIds.length === 0 && pickupTaskIds.length === 0) {
        setTotalCOD([]);
        setTotalCommission([]);
        setTotalPOP([]);
        setNetIncoming([]);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.post(
          `${API_ENDPOINT}/v1/driver-invoices/calculate-amounts/`,
          {
            delivery_task_ids: deliveryTaskIds,
            pickup_task_ids: pickupTaskIds,
            driver_id: driverId,
            purpose: purpose
          },
          config
        );

        setTotalCOD(response.data.total_cod);
        setTotalCommission(response.data.total_commission);
        setTotalPOP(response.data.total_pop);
        setNetIncoming(response.data.net_incoming);
      } catch (error) {
        console.error("Failed to fetch invoice amounts:", error);
        // Reset to empty on error
        setTotalCOD([]);
        setTotalCommission([]);
        setTotalPOP([]);
        setNetIncoming([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAmounts();
  }, [JSON.stringify(deliveryTaskIds), JSON.stringify(pickupTaskIds), driverId, config]);

  const netPositive = moneyToMetric(netIncoming).isPositive();

  const NetBox = (
    <Paper
      elevation={10}
      sx={{
        p: 2,
        minWidth: 200,
        bgcolor: netPositive ? green[100] : red[100],
        border: "2px solid",
        borderColor: netPositive ? green[500] : red[500],
        flexGrow: 1,
      }}
    >
      <Typography variant="subtitle2" color="text.secondary">
        Net Incoming
      </Typography>
      <Typography variant="h5" fontWeight="bold">
        {displayMoney(netIncoming)}
      </Typography>
    </Paper>
  );

  const CodBox = (
    <Paper elevation={2} sx={{ p: 2, minWidth: 180, flexGrow: 1 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Total COD
      </Typography>
      <Typography variant="h6">{displayMoney(totalCOD)}</Typography>
    </Paper>
  );

  const CommissionBox = (
    <Paper elevation={2} sx={{ p: 2, minWidth: 180, flexGrow: 1 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Total Commission
      </Typography>
      <Typography variant="h6">{displayMoney(totalCommission)}</Typography>
    </Paper>
  );

  const POPBox = (
    <Paper elevation={2} sx={{ p: 2, minWidth: 180, flexGrow: 1 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Total POP
      </Typography>
      <Typography variant="h6">{displayMoney(totalPOP)}</Typography>
    </Paper>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        mt: 2,
        flexDirection: {
          xs: "column",
          sm: "row",
        },
        justifyContent: {
          xs: "center",
          sm: "flex-start",
        },
        alignItems: {
          xs: "stretch",
          sm: "flex-start",
        },
        flexWrap: "wrap",
      }}
    >

          {NetBox}
          {[DriverInvoicePurpose.NET, DriverInvoicePurpose.COLLECTION].includes(purpose) && CodBox}
          {[DriverInvoicePurpose.NET, DriverInvoicePurpose.COMMISSION].includes(purpose) && CommissionBox}
          {[DriverInvoicePurpose.NET, DriverInvoicePurpose.COLLECTION].includes(purpose) && POPBox}
    </Box>
  );
};

export default DriverAmountsSummary;
